/** @jsxImportSource @emotion/react */

import {
  Typography,
  useTheme,
  List,
  CryptoCurrency,
  Button,
  ListItem,
  Copy,
  removeScaleFromMoney,
  useFormatCurrency,
  Loader,
  Tooltip,
  Icon,
  Modal,
} from '@quickbit/qb-design-system';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { cancelOrder } from 'api/getOrder';
import { useOrderId } from 'context';
import { useOrderDetails, usePoll } from 'hooks';
import { fetchOrderStatus, OrderStatusResponse } from 'pages/api';
import { redirectBasedOnStatus } from 'utils';
import { ErrorPage } from '../../components';
import { fetchBeneficiaryInfo } from './api';
import {
  BeneficiaryInfo,
  LineWithText,
  MerchantLogo,
  QRCode,
  WalletSelect,
} from './components';
import { usePreview } from './hooks';
import { createQRCodeAddressURL } from './utils';

export const Home = () => {
  const orderId = useOrderId();
  const navigate = useNavigate();

  const { spacing, palette, shape } = useTheme();
  const { formatMessage } = useIntl();
  const formatCurrency = useFormatCurrency();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { order, error, merchantLogo } = useOrderDetails();

  const [{ loading }, cancelOrderHandler] = useAsyncFn(async () => {
    await cancelOrder(orderId);
    navigate('/cancelled');
  }, [orderId]);

  const [{ value: beneficiaryInformation }, getBeneficiaryInfo] =
    useAsyncFn(async () => {
      if (order) {
        return fetchBeneficiaryInfo(order.order.merchantId);
      }
    }, [order]);

  useEffect(() => {
    getBeneficiaryInfo();
  }, [order, getBeneficiaryInfo]);

  const {
    preview,
    timer,
    error: previewError,
    loading: previewLoading,
    createPreview,
  } = usePreview();

  const selectedCurrency = preview?.cryptoAmount.currencyCode as CryptoCurrency;

  const checkStatus = useCallback(
    ({ status }: OrderStatusResponse) =>
      redirectBasedOnStatus(status, navigate) || null,
    [navigate]
  );

  useEffect(() => {
    if (order) {
      checkStatus({ status: order.order.status });
    }
  }, [order, checkStatus]);

  const poll = useCallback(() => fetchOrderStatus(orderId), [orderId]);

  const {
    start,
    error: pollingError,
    isPolling,
  } = usePoll({
    onPoll: checkStatus,
    poll,
  });

  useEffect(() => {
    if (!isPolling) start();
  }, [isPolling, start]);

  if (error || previewError || pollingError) {
    return (
      <ErrorPage
        error={error ?? previewError ?? pollingError!}
        tryAgainAction={() => navigate(0)}
      />
    );
  }

  const wallets = order?.wallets;
  return (
    <>
      {loading && <Loader />}
      {!loading && (
        <>
          <div
            css={{
              width: '100%',
              display: 'flex',
              justifyContent:
                merchantLogo?.enabled && !order?.order.hideQbBrand
                  ? 'space-between'
                  : 'flex-end',
            }}
          >
            {merchantLogo?.enabled && !order?.order.hideQbBrand && (
              <MerchantLogo
                merchantName={order?.order.merchantName}
                logoUrl={merchantLogo.logoUrl}
              />
            )}
            <div
              css={{
                cursor: 'pointer',
              }}
              onClick={() => setShowCancelModal(true)}
            >
              <Icon name="close" />
            </div>
          </div>
          <div
            css={{
              marginTop: spacing.xxs,
              marginBottom: spacing.xs,
            }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                marginBottom: spacing.xxxs,
                minHeight: spacing.s,
              }}
            >
              {order && !order.order.hideFiatAmount && (
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="subtitle">
                    <FormattedMessage
                      id="page.home.title"
                      values={{
                        amount:
                          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                          order &&
                          formatCurrency({
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            amount: order?.order.amount,
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            currencyCode: order?.order.currency,
                            scale: 0,
                          }),
                      }}
                    />
                  </Typography>
                  {order.order.customerFee && (
                    <div
                      css={{
                        display: 'flex',
                        gap: `calc(${spacing.xxxs} / 2)`,
                      }}
                    >
                      <Typography variant="body" color={palette.grey.default}>
                        <FormattedMessage
                          id="page.home.fee"
                          values={{
                            amount:
                              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                              order &&
                              formatCurrency({
                                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                                amount: order?.order.customerFee,
                                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                                currencyCode: order?.order.currency,
                                scale: 0,
                              }),
                          }}
                        />
                      </Typography>
                      <Tooltip
                        title={formatMessage({
                          id: 'page.home.feeDescription',
                        })}
                      >
                        <div
                          css={{
                            svg: {
                              color: palette.grey.default,
                            },
                          }}
                        >
                          <Icon name="info" size={spacing.xxs} />
                        </div>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
              <div css={{ gridColumn: '2 / span 1', justifySelf: 'end' }}>
                {preview && timer}
              </div>
            </div>
            <Typography variant="body">
              <FormattedMessage
                id={
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  !selectedCurrency
                    ? 'page.home.subtitle1'
                    : 'page.home.subtitle2'
                }
              />
            </Typography>
          </div>
          {previewLoading && <Loader />}
          {!previewLoading && preview && (
            <>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: spacing.xxs,
                }}
              >
                <div css={{ margin: spacing.s }}>
                  <div css={{ textTransform: 'uppercase' }}>
                    <Typography variant="bodyBold">
                      {formatMessage({ id: 'page.home.totalSum' })}
                    </Typography>
                  </div>
                  <div
                    css={{
                      display: 'flex',
                      gap: `calc(${spacing.xxxs} / 2)`,
                    }}
                  >
                    <div
                      css={{
                        marginTop: `calc(${spacing.xxxs} / 4)`,
                      }}
                    >
                      <Typography
                        variant="bodySmallBold"
                        color={palette.grey.default}
                      >
                        {formatMessage({ id: 'page.home.excludingNFees' })}
                      </Typography>
                    </div>
                    <Tooltip
                      title={formatMessage({
                        id: 'page.home.excludingNFeesInfo',
                      })}
                      placement="top"
                    >
                      <div css={{ svg: { color: palette.grey.default } }}>
                        <Icon name="info" size={spacing.xxs} />
                      </div>
                    </Tooltip>
                  </div>
                  {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                  {preview && (
                    <Typography variant="heading1">
                      {formatCurrency(preview.cryptoAmount)}
                    </Typography>
                  )}
                </div>
                <div
                  css={{
                    display: 'flex',
                    border: `1px solid ${palette.grey.x100}`,
                    padding: spacing.xxxs,
                    borderRadius: shape.borderRadius,
                  }}
                >
                  <QRCode
                    value={createQRCodeAddressURL(
                      preview.cryptoAmount,
                      preview.destinationAddress
                    )}
                    currency={
                      preview.cryptoAmount.currencyCode as CryptoCurrency
                    }
                  />
                </div>
              </div>
            </>
          )}
          {!previewLoading && wallets && (
            <List>
              <WalletSelect
                wallets={wallets}
                value={selectedCurrency}
                onChange={(wallet) => createPreview(wallet.currency)}
                firstSelect={!preview}
              />
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
              {preview && selectedCurrency ? (
                <>
                  <ListItem
                    title={formatMessage({ id: 'address' })}
                    subtitle={preview.destinationAddress}
                    action={<Copy textToCopy={preview.destinationAddress} />}
                  />
                  <ListItem
                    title={formatMessage({ id: 'page.home.amount' })}
                    subtitle={formatCurrency(preview.cryptoAmount)}
                    action={
                      <Copy
                        textToCopy={removeScaleFromMoney(
                          preview.cryptoAmount
                        ).toString()}
                      />
                    }
                  />
                </>
              ) : (
                <></>
              )}
            </List>
          )}
          {order && !order.order.hideQbBrand && preview && (
            <div css={{ marginTop: spacing.xxs }}>
              <LineWithText text={formatMessage({ id: 'page.home.or' })} />
              <div css={{ marginTop: spacing.xxs, marginBottom: spacing.xs }}>
                <Button
                  text={formatMessage({ id: 'page.home.useApp' })}
                  onClick={() => {
                    window.open(
                      `${process.env.REACT_APP_WEB_APP_BASE_URL}/${preview.cryptoAmount.currencyCode}/send/preview?receiveOrderPreviewId=${preview.uuid}`,
                      '_blank'
                    );
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      {showCancelModal && (
        <Modal
          title={formatMessage({ id: 'page.home.cancelOrder' })}
          subtitle={formatMessage({ id: 'page.home.cancelOrder.confirm' })}
          primaryButtonProps={{
            text: formatMessage({ id: 'page.home.cancelOrder.yes' }),
            onClick: cancelOrderHandler,
          }}
          secondaryButtonProps={{
            text: formatMessage({ id: 'page.home.cancelOrder.no' }),
            onClick: () => setShowCancelModal(false),
          }}
          closeModal={() => setShowCancelModal(false)}
        />
      )}
      {!previewLoading && beneficiaryInformation && (
        <BeneficiaryInfo info={beneficiaryInformation} />
      )}
    </>
  );
};
